"use client";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2 } from "lucide-react";
import { ReactNode } from "react";
import { Typography } from "./typography";
import { cn } from "@drpcrd/utils/shared";

type Props = {
  id: string;
  leftElement: ReactNode;
  title: ReactNode;
  rightElement?: ReactNode;
  isDraggable?: boolean;
  onDelete?: () => void;
  className?: string;
};

/**
 * Individual draggable row item with drag handle, custom left element, title, and optional right element or delete button.
 */
export const DraggableRowItem = ({
  id,
  leftElement,
  title,
  rightElement,
  isDraggable = true,
  onDelete,
  className,
}: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isDraggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "bg-bg-primary shadow-light flex items-center gap-2 rounded-lg p-2",
        isDragging && "opacity-50",
        className,
      )}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className={cn(
          "touch-none",
          isDraggable
            ? "cursor-grab active:cursor-grabbing"
            : "cursor-not-allowed opacity-50",
        )}
        {...attributes}
        {...listeners}
        disabled={!isDraggable}
        aria-disabled={!isDraggable}
        aria-label={
          !isDraggable
            ? "Drag handle disabled while item is processing"
            : "Drag to reorder"
        }
      >
        <GripVertical className="text-text-neutral h-3 w-3" />
      </button>

      {/* Left Element */}
      <div className="flex items-center justify-center">{leftElement}</div>

      {/* Title */}
      {typeof title === "string" ? (
        <Typography size="sm" weight="medium" className="flex-1">
          {title}
        </Typography>
      ) : (
        <div className="flex-1">{title}</div>
      )}

      {/* Right Element or Delete Button */}
      {rightElement ? (
        <div>{rightElement}</div>
      ) : (
        onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-text-neutral hover:text-text-error cursor-pointer transition-colors"
          >
            <Trash2 className="h-2 w-2" />
          </button>
        )
      )}
    </div>
  );
};
